// components/TodayStockInList.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../utils/api";
import "../styles/TodayStockInList.css";

const STORAGE_KEY = "todayStockInListState";

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatTime = (datetime) => {
  if (!datetime) return "--:--";
  return new Date(datetime).toLocaleTimeString("ja-JP", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const formatMoney = (value) => Number(value ?? 0).toLocaleString("ja-JP");

const TodayStockInList = ({ storeId }) => {
  const navigate = useNavigate();

  // 商品詳細画面から戻ったときに、表示日・カテゴリー・スクロール位置を復元する。
  const savedStateRef = useRef(undefined);
  if (savedStateRef.current === undefined) {
    try {
      savedStateRef.current = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null");
    } catch {
      savedStateRef.current = null;
    }
  }

  const [selectedDate, setSelectedDate] = useState(
    () => savedStateRef.current?.selectedDate || formatLocalDate(new Date())
  );
  const [selectedCategory, setSelectedCategory] = useState(
    () => savedStateRef.current?.selectedCategory || ""
  );
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const listRef = useRef(null);
  const restoreScrollRef = useRef(savedStateRef.current?.scrollTop ?? null);
  const previousDateRef = useRef(selectedDate);

  const todayStr = formatLocalDate(new Date());
  const isToday = selectedDate === todayStr;

  const moveDate = (amount) => {
    const [year, month, day] = selectedDate.split("-").map(Number);
    const nextDate = new Date(year, month - 1, day);
    nextDate.setDate(nextDate.getDate() + amount);
    const nextStr = formatLocalDate(nextDate);
    if (nextStr > formatLocalDate(new Date())) return;
    setSelectedDate(nextStr);
  };

  // カテゴリー取得
  useEffect(() => {
    let cancelled = false;
    apiRequest("inventory/categorys/", "GET")
      .then((response) => {
        if (!cancelled) {
          setCategories(Array.isArray(response) ? response : response?.results ?? []);
        }
      })
      .catch((err) => {
        if (!cancelled) console.error("入庫一覧のカテゴリー取得失敗:", err);
      });
    return () => { cancelled = true; };
  }, []);

  // 初回・日付変更時に取得。今日だけ30秒ごとに更新（通信完了後に予約）。
  useEffect(() => {
    let cancelled = false;
    let timeoutId = null;

    if (!storeId) {
      setItems([]);
      setLoading(false);
      return () => { cancelled = true; };
    }

    setLoading(true);

    const fetchItems = async () => {
      try {
        const query = new URLSearchParams({
          date: selectedDate,
          store_id: String(storeId),
        });
        const response = await apiRequest(
          `inventory/inventory_adjustments/stock-in-list/?${query}`,
          "GET"
        );
        if (!cancelled) {
          setItems(Array.isArray(response) ? response : []);
          setError("");
        }
      } catch (err) {
        if (!cancelled) {
          console.error("入庫商品取得失敗:", err);
          setError("入庫履歴を取得できませんでした");
          setItems([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          if (isToday) timeoutId = setTimeout(fetchItems, 30000);
        }
      }
    };

    fetchItems();
    return () => {
      cancelled = true;
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
  }, [selectedDate, storeId, isToday]);

  const filteredItems = useMemo(() => {
    if (!selectedCategory) return items;
    return items.filter(
      (item) => String(item.category_id) === String(selectedCategory)
    );
  }, [items, selectedCategory]);

  const totalQuantity = useMemo(
    () => filteredItems.reduce((sum, item) => sum + Number(item.quantity ?? 0), 0),
    [filteredItems]
  );

  // 日付変更時は先頭へ。30秒更新時にはスクロールを動かさない。
  useEffect(() => {
    if (previousDateRef.current !== selectedDate) {
      if (listRef.current) listRef.current.scrollTop = 0;
      previousDateRef.current = selectedDate;
      restoreScrollRef.current = null;
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [selectedDate]);

  // 商品詳細から戻ったときだけ、読み込み完了後に位置を復元する。
  useEffect(() => {
    if (loading || restoreScrollRef.current === null) return;
    const savedScrollTop = restoreScrollRef.current;
    const animationId = requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = savedScrollTop;
      restoreScrollRef.current = null;
      sessionStorage.removeItem(STORAGE_KEY);
    });
    return () => cancelAnimationFrame(animationId);
  }, [loading, filteredItems]);

  const navigateToDetail = (inventoryId) => {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        selectedDate,
        selectedCategory,
        scrollTop: listRef.current?.scrollTop ?? 0,
      })
    );
    navigate(`/Inventory_detail/${inventoryId}/${storeId}`);
  };

  return (
    <div className="TodayStockInList-container">
      <div className="TodayStockInList-toolbar">
        <button type="button" onClick={() => moveDate(-1)}>◀ 前日</button>
        <span className="TodayStockInList-date">{selectedDate}</span>
        <button type="button" onClick={() => moveDate(1)} disabled={isToday}>
          翌日 ▶
        </button>

        <select
          aria-label="カテゴリーを選択"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="">全カテゴリー</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.category_name}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => {
            setSelectedDate(formatLocalDate(new Date()));
            setSelectedCategory("");
          }}
        >
          今日に戻す
        </button>
      </div>

      <div className="TodayStockInList-header">
        <h3>📦 入庫した商品</h3>
        <span>{filteredItems.length}件 ／ {totalQuantity}点</span>
      </div>

      <div className="TodayStockInList-scroll" ref={listRef}>
        {loading && <p className="TodayStockInList-message">読み込み中...</p>}
        {!loading && error && (
          <p className="TodayStockInList-message" role="alert">{error}</p>
        )}
        {!loading && !error && filteredItems.length === 0 && (
          <p className="TodayStockInList-message">この日の入庫商品はありません。</p>
        )}
        <ul className="TodayStockInList-list">
          {filteredItems.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className="TodayStockInList-item"
                onClick={() => navigateToDetail(item.inventory_id)}
              >
                <img
                  src={item.sku
                    ? `/media/product_images/${item.sku}.jpg`
                    : "/media/system_images/noimage.jpg"}
                  alt=""
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "/media/system_images/noimage.jpg";
                  }}
                />
                <span className="TodayStockInList-info">
                  <span className="TodayStockInList-name">{item.item_name}</span>
                  <span className="TodayStockInList-meta">
                    {item.category_name} ／ 売価: ￥{formatMoney(item.sell_price)} ／ 在庫: {item.stock}
                  </span>
                  <span className="TodayStockInList-time">{formatTime(item.datetime)}</span>
                </span>
                <span className="TodayStockInList-quantity">+{item.quantity} 点</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default TodayStockInList;
